//! Pixi placeholders for HTML charts (Plotly) that straddle a freeze boundary.
//!
//! The live chart iframe lives in a DOM pane clipped to the contiguous (untorn)
//! region, so once the body scrolls past the freeze line the iframe no longer
//! covers the torn-away slices. Those slices are filled here with a pixi sprite
//! of the chart's pre-rendered image (plus a border/background).
//!
//! Rendered above the grid lines (so it covers them, like the mobile chart
//! placeholders) and also re-rendered pinned inside the frozen panes by
//! FrozenPanes. The live iframe always layers above the whole canvas, so
//! wherever the iframe shows it hides the placeholder; wherever it's clipped
//! away the placeholder shows through — a seamless chart that tears at the
//! freeze line(s) when scrolled.
//!
//! The per-chart rendering (border, background, aspect-fit sprite) mirrors
//! MobileChartPlaceholders. On desktop, shown when torn and the chart spans a
//! freeze line. On mobile, only while the chart is selected (live iframe) —
//! unselected charts use MobileChartPlaceholders for torn slices instead.

import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import { htmlCellsHandler } from '@/app/gridGL/HTMLGrid/htmlCells/htmlCellsHandler';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import { chartSpansFreezeBoundary, getFrozenLayout } from '@/app/gridGL/pixiApp/frozenLayout';
import { getCSSVariableTint } from '@/app/helpers/convertColor';
import { isMobileMode } from '@/app/helpers/isMobile';
import type { JsHtmlOutput } from '@/app/quadratic-core-types';
import { BaseTexture, Container, Graphics, Sprite, Texture } from 'pixi.js';

const BORDER_WIDTH = 1;

interface ChartPlaceholder {
  data: JsHtmlOutput;
  container: Container;
  background: Graphics;
  sprite: Sprite;
  border: Graphics;
  textureLoading: boolean;
  textureReady: boolean;
  // Texture mounted from the chart's data URL; tracked for explicit disposal
  // (data URLs are unique per image and would otherwise leak in Pixi's caches).
  loadedTexture?: Texture;
}

export class ChartPlaceholders extends Container {
  private charts: Map<string, ChartPlaceholder> = new Map();

  constructor() {
    super();
    events.on('htmlOutput', this.handleHtmlOutput);
    events.on('htmlUpdate', this.handleHtmlUpdate);
  }

  destroy() {
    events.off('htmlOutput', this.handleHtmlOutput);
    events.off('htmlUpdate', this.handleHtmlUpdate);
    for (const chart of this.charts.values()) {
      this.evictTexture(chart);
    }
    this.charts.clear();
    super.destroy({ children: true });
  }

  private getKey(data: JsHtmlOutput): string {
    return `${data.sheet_id}:${data.x}:${data.y}`;
  }

  private handleHtmlOutput = (htmlCells: JsHtmlOutput[]) => {
    const newKeys = new Set(htmlCells.filter((c) => c.html).map((c) => this.getKey(c)));
    for (const [key, chart] of this.charts) {
      if (!newKeys.has(key)) this.removeChart(key, chart);
    }
    for (const data of htmlCells) {
      if (data.html) this.addOrUpdate(data);
    }
  };

  private handleHtmlUpdate = (data: JsHtmlOutput) => {
    const key = this.getKey(data);
    if (data.html) {
      this.addOrUpdate(data);
    } else {
      const chart = this.charts.get(key);
      if (chart) this.removeChart(key, chart);
    }
  };

  private addOrUpdate(data: JsHtmlOutput) {
    const key = this.getKey(data);
    const existing = this.charts.get(key);
    if (existing) {
      // re-fetch the image if the source changed or the chart was resized (the
      // plotly.js render is size-specific)
      const changed =
        existing.data.chart_image !== data.chart_image || existing.data.w !== data.w || existing.data.h !== data.h;
      existing.data = data;
      if (changed) {
        this.evictTexture(existing);
        existing.textureReady = false;
        existing.textureLoading = false;
      }
      if (chartSpansFreezeBoundary(data)) {
        this.ensureTexture(existing);
      }
      return;
    }

    const container = new Container();
    // order: background (covers grid lines while loading), then the chart image
    // filling the full rect, then the border on top so the sprite can't cover it
    const background = container.addChild(new Graphics());
    const sprite = container.addChild(new Sprite(Texture.EMPTY));
    sprite.visible = false;
    const border = container.addChild(new Graphics());
    container.visible = false;
    this.addChild(container);
    const chart: ChartPlaceholder = {
      data,
      container,
      background,
      sprite,
      border,
      textureLoading: false,
      textureReady: false,
    };
    this.charts.set(key, chart);
    if (chartSpansFreezeBoundary(data)) {
      this.ensureTexture(chart);
    }
  }

  private removeChart(key: string, chart: ChartPlaceholder) {
    this.evictTexture(chart);
    chart.container.destroy({ children: true });
    this.charts.delete(key);
  }

  private evictTexture(chart: ChartPlaceholder) {
    const texture = chart.loadedTexture;
    chart.loadedTexture = undefined;
    if (chart.sprite.texture === texture) {
      chart.sprite.texture = Texture.EMPTY;
    }
    if (texture && texture !== Texture.EMPTY) {
      texture.destroy(true);
    }
  }

  // Loads the chart image ahead of the first torn frame so scrolling past a
  // freeze line does not flash the placeholder icon.
  private ensureTexture(chart: ChartPlaceholder) {
    if (chart.textureReady || chart.textureLoading) return;
    chart.textureLoading = true;

    const key = this.getKey(chart.data);
    const upgradeFromPlotly = (src: string | undefined) => {
      const url = src ?? chart.data.chart_image ?? undefined;
      if (!url) {
        chart.textureLoading = false;
        return;
      }
      this.applyTextureUrl(chart, key, url, chart.textureReady);
    };

    if (chart.data.chart_image) {
      this.applyTextureUrl(chart, key, chart.data.chart_image, false);
      // Prefer a plotly capture when available, but only before the torn slice is
      // on screen so swapping textures does not flash over the live iframe. This
      // always replaces the chart_image applied above, so it is an upgrade
      // regardless of whether that base texture has finished decoding yet.
      htmlCellsHandler
        .getPlaceholderImage(chart.data.sheet_id, Number(chart.data.x), Number(chart.data.y))
        .then((src) => {
          if (!this.charts.has(key) || !src || src === chart.data.chart_image) return;
          if (chart.container.visible) return;
          this.applyTextureUrl(chart, key, src, true);
        })
        .catch(() => undefined);
      return;
    }

    htmlCellsHandler
      .getPlaceholderImage(chart.data.sheet_id, Number(chart.data.x), Number(chart.data.y))
      .then(upgradeFromPlotly)
      .catch(() => upgradeFromPlotly(chart.data.chart_image ?? undefined));
  }

  private applyTextureUrl(chart: ChartPlaceholder, key: string, url: string, isUpgrade: boolean) {
    if (!this.charts.has(key)) return;

    const baseTexture = new BaseTexture(url);
    const mount = () => {
      // Identity check, not just the key: the chart may have been removed (its
      // container destroyed) and re-added at the same cell while this texture
      // was decoding, in which case `chart` here is the destroyed one.
      if (this.charts.get(key) !== chart) {
        baseTexture.destroy();
        return;
      }
      this.evictTexture(chart);
      const texture = new Texture(baseTexture);
      chart.loadedTexture = texture;
      chart.sprite.texture = texture;
      chart.sprite.visible = true;
      chart.textureLoading = false;
      chart.textureReady = true;
      if (chart.container.visible) {
        this.updateChartPosition(chart);
      }
    };

    if (baseTexture.valid) {
      mount();
      return;
    }

    baseTexture.once('loaded', mount);
    baseTexture.once('error', () => {
      if (!isUpgrade) {
        chart.textureLoading = false;
      }
    });
  }

  // Position/size the container, background, sprite, and border so the
  // placeholder exactly overlays where the live iframe renders the chart. The
  // sprite fills the FULL chart rect (the same rect the iframe fills) — the
  // freeze clipping culls whatever is off-pane — so the visible part lines up
  // seamlessly with the iframe at the tear.
  private updateChartPosition(chart: ChartPlaceholder) {
    const sheet = sheets.getById(chart.data.sheet_id);
    if (!sheet) return;
    const { data, container, background, border, sprite } = chart;

    // A texture 'update' listener below can fire after the chart was removed; a
    // destroyed container has a null transform, so touching `position` throws.
    if (container.destroyed) return;

    // y+1 / h-1 to skip the code-cell header row, matching the iframe placement.
    const rect = sheet.getScreenRectangle(Number(data.x), Number(data.y) + 1, data.w ?? 1, (data.h ?? 1) - 1);
    container.position.set(rect.x, rect.y);

    // opaque white background (matches the iframe's #fff) so the grid lines are
    // covered while the texture decodes / in any letterbox area
    background.clear();
    background.beginFill(0xffffff);
    background.drawRect(0, 0, rect.width, rect.height);
    background.endFill();

    const texture = sprite.texture;
    const hasImage = chart.textureReady && texture.valid && texture.width > 0 && texture.height > 0;
    sprite.visible = hasImage;
    if (hasImage) {
      // Scale to fit the full rect preserving the image's aspect ratio, then
      // center within the full rect (so the off-pane/culled part is accounted
      // for and the visible slice lines up with the iframe).
      const imageAspect = texture.width / texture.height;
      const innerWidth = rect.width - BORDER_WIDTH * 2;
      const innerHeight = rect.height - BORDER_WIDTH * 2;
      const cellAspect = innerWidth / innerHeight;
      let renderWidth: number;
      let renderHeight: number;
      if (imageAspect > cellAspect) {
        renderWidth = innerWidth;
        renderHeight = renderWidth / imageAspect;
      } else {
        renderHeight = innerHeight;
        renderWidth = renderHeight * imageAspect;
      }
      sprite.width = renderWidth;
      sprite.height = renderHeight;
      sprite.position.set((rect.width - renderWidth) / 2, (rect.height - renderHeight) / 2);
    } else if (chart.textureReady) {
      sprite.texture.once('update', () => this.updateChartPosition(chart));
    }

    // border on top so it isn't covered; matches the live chart's 1px
    // muted-foreground border
    border.clear();
    border.lineStyle(BORDER_WIDTH, getCSSVariableTint('muted-foreground'), 1, 0);
    border.drawRect(0, 0, rect.width, rect.height);
  }

  // Driven each frame from Content.update().
  update() {
    const mobile = isMobileMode();
    const torn = !!getFrozenLayout()?.torn;
    for (const chart of this.charts.values()) {
      const spans = chartSpansFreezeBoundary(chart.data);
      const onCurrentSheet = chart.data.sheet_id === sheets.current;
      if (onCurrentSheet && spans) {
        this.ensureTexture(chart);
      }
      let show = torn && onCurrentSheet && spans;
      // On mobile the pixi chart image is shown by MobileChartPlaceholders until
      // the chart is selected; only then do we fill torn-away slices (alongside
      // the live iframe in the contiguous DOM pane).
      if (mobile) {
        show = show && content.mobileChartPlaceholders.isChartActivated(chart.data);
      }
      chart.container.visible = show;
      if (!show) continue;
      this.updateChartPosition(chart);
    }
  }
}
